/**
 * One hundred rounds, generated deterministically from a seed.
 *
 * ## The shape of the thing
 *
 * BTD6's round list is not a difficulty function with noise on top. It is a
 * *curriculum*. A new bloon type shows up once, alone, in a round you cannot
 * lose, and then two rounds later it arrives in bulk. Every tenth round is a
 * visible step up. The round after a spike is quieter than the one before it,
 * so the player gets a beat to spend money in. Rounds 20/40/60/80/100 stop the
 * curve and put one enormous thing on the track.
 *
 * This file reproduces that structure explicitly rather than hoping it falls
 * out of a random generator:
 *
 * 1. {@link baseRbe} is an authored curve through fourteen anchor points,
 *    interpolated in log space. Its shape is not a smooth decay: it grows about
 *    19% a round through act I, peaks near 23% across acts II and III, and
 *    settles to 5–9% a round across acts IV–VII. That is the shape of the
 *    *player's* power curve read back at it — see docs/BALANCE.md §2.2.
 * 2. {@link beat} multiplies that curve by the *rhythm* — decade steps,
 *    post-spike breathers, mid-decade bumps. It is a pure function of the
 *    round number, so the rhythm is identical for every seed.
 * 3. The roster decides *what* is in the round, and the RBE target decides
 *    *how much*. Counts are solved backwards from the target, which is what
 *    makes the curve checkable (see `tests/waves.test.ts`) rather than
 *    emergent.
 *
 * The seed only ever picks *which* families appear and how their spawns are
 * staggered. It never moves the difficulty curve. Two seeds play differently
 * and are exactly as hard as each other.
 *
 * ## Acts
 *
 * The hundred rounds are divided into seven acts by `game/Levels.ts`, and this
 * file reads three things from it: the act's centreline length (which prices
 * shield regeneration in {@link demandOf}), whether a round opens an act (which
 * lengthens the prep window, because the board was refunded), and nothing else.
 * The *curve* is authored here; the *arena* it is fought in is authored there.
 *
 * ## RBE
 *
 * "Red bloon equivalent" in BTD6 terms: total effective hit points, counting
 * shields and the whole splitter chain. `EnemyCatalog.totalRbe` does the walk.
 * RBE is the number the danger meter shows and the number `Balance.ts` checks
 * a loadout against.
 *
 * ## Two scales, not one
 *
 * Non-boss enemies scale by {@link hpScale}, a four-segment exponential that
 * tops out near 1,000x. Bosses scale by {@link bossHpScale}, solved against the
 * authored magnitudes in {@link BOSS_RBE}. They need separate treatment because
 * `EnemyCatalog` authors bosses as absolute end-game statlines (SINGULARITY at
 * 42k hp) while authoring everything else as round-1 bases (grunt at 40hp), and
 * because only single-target damage counts against one body. One curve cannot
 * serve both. See docs/BALANCE.md §3.
 */

import { WAVES } from '@/core/config';
import type { PhosphorName } from '@/core/palette';
import { seedFrom } from '@/core/rng';
import type { Wave, WaveGroup, World } from '@/core/types';
import { BOSS_IDS, enemyById, totalBodies, totalRbe } from './EnemyCatalog';
import { actOf, isActOpening } from './Levels';
import {
  awardEarlyStart, awardRoundBonus, checkTransitions, earlyStartBonus, payoutScale,
} from './Economy';

// ────────────────────────────────────────────────────────────── the curve ──

/**
 * Authored RBE anchors. Interpolated in log space between them.
 *
 * Read the ratios, not the absolutes. Per-round growth runs 19% → 23% → 22%
 * across the first three acts and 9% → 7.7% → 5.5% → 5.7% across the last four.
 *
 * **Anchors sit on and inside act boundaries, not every ten rounds.** An act is
 * where the player's board changes shape — slots, map, unlocks, an ability — so
 * it is where the curve has to be steerable, and a ten-round grid put the
 * control points in the wrong places.
 *
 * **The tail used to be flat and that was a ceiling, not a choice.** Past round
 * 70 a fixed twenty-four slot board was within a few purchases of finished, the
 * damage available stopped climbing, and the curve grew 0.6% a round for thirty
 * rounds. `Levels.ts` raised the ceiling by widening the board, lengthening the
 * corridor and adding the ability picks; rounds 70–100 now grow 4.3% a round.
 * docs/BALANCE.md §8.2 is the before and after.
 *
 * The curve is still bounded by what the *roster* can carry, though far less
 * tightly than it was: rounds 35, 36 and 48 used to land at 69–78% of these
 * anchors with every group pinned at its size cap, and round 48 at 80% is the
 * only one left under 94%. See docs/BALANCE.md §6.4 before raising them further,
 * and §7.4 before assuming that raising them makes anything harder — measured
 * across four acts, it does the opposite.
 */
export const RBE_ANCHORS: readonly (readonly [number, number])[] = [
  [1, 340],
  [12, 1_250],
  [18, 3_600],
  [24, 10_500],
  [32, 31_000],
  [40, 108_000],
  [50, 457_000],
  [60, 1_205_000],
  [68, 2_870_000],
  [75, 4_970_000],
  [82, 9_820_000],
  [88, 13_600_000],
  [94, 40_000_000],
  [100, 66_000_000],
];

/**
 * Rhythm multipliers. Every value here is a deliberate feel decision:
 *
 * - `decade` — round 10, 30, 50... is the visible step. The player should
 *   notice the danger meter jump before the round starts.
 * - `breather` — the round *after* a step is materially quieter. This is where
 *   BTD6 lets you buy the upgrade you were saving for.
 * - `midBump` / `midDip` — a smaller wave inside the decade so the ten-round
 *   block has internal shape instead of a flat run into the next step.
 * - `boss` — 1.00. Boss rounds sit *on* the curve, not above it. The boss body
 *   is authored separately in {@link BOSS_RBE} and adds to the round; stacking
 *   a multiplier on top of that made the escorts, rather than the boss, the
 *   thing that ended runs at rounds 60, 80 and 100.
 * - `debut` — the round that introduces a new family is *easier* than its
 *   neighbours. You meet the thing before you have to solve it.
 */
export const BEAT = {
  decade: 1.30,
  breather: 0.80,
  midBump: 1.10,
  midDip: 0.92,
  boss: 1.00,
  debut: 0.85,
} as const;

/**
 * Effective hit points of the boss body itself, authored per boss round.
 *
 * **This used to be a share of the round and that was wrong.** A boss is one
 * body, so only single-target damage counts against it: pierce, splash and
 * chain all collapse to one target. The rest of a round is answered by exactly
 * those multipliers, which is why round RBE can grow 40,000x across a hundred
 * rounds while a board's *single-target* output grows perhaps 400x. Peg the
 * boss to a fixed share of that round total and the boss curve outruns the only
 * damage that can touch it — measured at `BOSS_SHARE = 0.45`, round 80's
 * QUICKSILVER needed six times the single-target DPS any affordable board could
 * produce, and rounds 60/80/100 were unclearable by every recipe.
 *
 * So boss magnitude is authored directly, on its own curve: 400 → 1,600,000
 * across eighty rounds, running 7.8% → 4.2% → 3.7% → 2.5% → 2.4% of its round.
 *
 * Those shares are far below the previous revision's 16% → 18%, and the reason
 * is that none of it was reaching the game. `Enemy.acquire` took a
 * `(round) => number` hook, so every boss spawned on the *trash* curve: round
 * 100's SINGULARITY carried 39 million hit points behind 12 million of
 * overshield regenerating at 600,000 a second, and `tests/simcheck.test.ts`
 * measured all five bosses walking the full length of every map untouched — a
 * guaranteed 235 lives across a run, on top of everything else. With the hook
 * widened to `(round, archetypeId)` the numbers here finally bind, and they had
 * to come down to a size a board can remove from *one* body.
 *
 * `Balance.curveTable` reports the realised share so a future edit that lets it
 * drift back upward shows up in docs/BALANCE.md.
 */
export const BOSS_RBE: Readonly<Record<number, number>> = {
  20: 400,
  40: 4_500,
  60: 45_000,
  80: 200_000,
  100: 1_600_000,
};

/** Hard ceiling on bodies queued by one round, so the enemy pool cannot starve. */
export const MAX_BODIES_PER_ROUND = 900;

/** Bodies one group may contribute, split chain included. */
const CAP_BODIES_PER_GROUP = 220;


/**
 * Group size caps, at act IV's board width. Clusters are allowed to be much
 * larger — that is the point. Pool safety is `CAP_BODIES_PER_GROUP`'s job, so
 * these are readability caps: a queue of more than ninety identical bodies stops
 * reading as a formation.
 */
const CAP_CLUSTER = 100;
const CAP_QUEUE = 90;

/**
 * How much of those caps an act is allowed, as a fraction of act IV's.
 *
 * A cap on *bodies* has to move with the board that shoots them, and these two
 * did not. Act I owns ten slots, three tower families and about £2,800; round 10
 * sent it eighty-eight swarmlings inside a four-second cluster and it lost
 * thirty-nine lives on the round, measured. The same flat ninety put ninety
 * runners and a hundred hornets into round 39 against a fourteen-tower board and
 * cost sixty-two more. Neither round is *large* — both land on their RBE target
 * — they are simply shaped as a wall, and a wall is priced by how many guns can
 * face it.
 *
 * Slots are the honest proxy for that: they are the only term in
 * `Levels.capacityIndex` that counts guns. Floored at a third so an act cannot
 * lose the swarm as a mechanic, and the budget the caps refuse is not lost — the
 * redistribution pass and the top-up in {@link buildWave} spend it on the rest of
 * the round, which is exactly the shape wanted. Fewer, in more places.
 */
function capScale(round: number): number {
  return Math.max(0.34, Math.min(1, actOf(round).slots / 24));
}

/**
 * Bodies a regenerating group may hold, which is much lower than the rest.
 *
 * An overshield's cost is superlinear in the size of the group carrying it. A
 * board suppresses a body by hitting it inside `Enemy.SHIELD_REGEN_DELAY`, and
 * the hits it has to give are fixed, so doubling the group halves the rate each
 * body takes and hands the whole difference back as regeneration. Ninety works
 * as a readability cap for a queue of grunts and priced a queue of `shielded`
 * at three times its own effective hit points — measured on rounds 31 and 32,
 * coverage 0.77 and eighty lives against act III's eighteen slots.
 */
const CAP_REGEN = 26;

/** True when a body puts its overshield back faster than it is worth ignoring. */
function regenerates(id: string): boolean {
  const a = enemyById(id);
  return a.shield > 0 && a.shieldRegenPerSec > 0;
}

/** Bodies one group of `id` may hold on `round`, before the body and budget caps. */
function groupCap(id: string, cluster: boolean, round: number): number {
  const scale = capScale(round);
  if (cluster) return Math.max(8, Math.round(CAP_CLUSTER * scale));
  const a = enemyById(id);
  const flat = a.shield > 0 && a.shieldRegenPerSec > 0 ? CAP_REGEN : CAP_QUEUE;
  return Math.max(6, Math.round(flat * scale));
}

/**
 * Largest share of a round's budget one top-up group may carry.
 *
 * The top-up loop spends whatever the size caps refused, and it spends it on
 * the heaviest thing available. Unbounded, that is one queue holding most of
 * the round — which reads as a wall rather than as a wave, and prices the round
 * as pure single-target demand because splash and pierce have nothing else to
 * catch.
 */
const TOP_UP_SHARE = 0.22;

/**
 * Largest share of a round's budget any one solved group may carry, given how
 * many groups the round is built from.
 *
 * It has to depend on the group count. A round of two groups cannot be built
 * with a flat 42% cap — the pair cannot cover the budget, the top-up fires and
 * splits the round into five, and act I's rounds stop being three big shapes
 * arriving one after another. A round of six groups with one of them at 42% is
 * the wall this cap exists to stop.
 */
function groupShare(groups: number): number {
  return Math.min(0.72, Math.max(0.28, 1.4 / Math.max(1, groups)));
}

/** Seconds a queued (non-cluster) group tries to spread its spawns across. */
const SPAWN_WINDOW = 20;

const clampRound = (r: number): number => (r < 1 ? 1 : r > WAVES.count ? WAVES.count : Math.floor(r));

/** The authored curve, before rhythm. Log-space interpolation between anchors. */
export function baseRbe(round: number): number {
  const r = clampRound(round);
  for (let i = 1; i < RBE_ANCHORS.length; i++) {
    const [r1, v1] = RBE_ANCHORS[i];
    if (r > r1) continue;
    const [r0, v0] = RBE_ANCHORS[i - 1];
    const t = (r - r0) / (r1 - r0);
    return Math.exp(Math.log(v0) + t * (Math.log(v1) - Math.log(v0)));
  }
  return RBE_ANCHORS[RBE_ANCHORS.length - 1][1];
}

export function isBossRound(round: number): boolean {
  return (WAVES.bossRounds as readonly number[]).includes(round);
}

/** Boss archetype id for a round, or null. Indexed against `WAVES.bossRounds`. */
export function bossFor(round: number): string | null {
  const i = (WAVES.bossRounds as readonly number[]).indexOf(round);
  return i < 0 ? null : BOSS_IDS[i] ?? null;
}

/** Pure function of the round number. Identical across every seed. */
export function beat(round: number): number {
  const r = clampRound(round);
  if (isBossRound(r)) return BEAT.boss;
  // The opening three rounds are the tutorial. No rhythm, just a ramp.
  if (r <= 3) return 1;
  const m = r % 10;
  if (m === 0) return BEAT.decade;
  if (m === 1) return BEAT.breather;
  if (m === 5) return BEAT.midBump;
  if (m === 6) return BEAT.midDip;
  return 1;
}

/**
 * The RBE a round aims for. Composition is then solved to hit this number, so
 * this is the curve the tests assert and the danger meter reads.
 */
export function targetRbe(round: number): number {
  const r = clampRound(round);
  const relief = debutAt(r) !== null ? BEAT.debut : 1;
  return baseRbe(r) * beat(r) * relief;
}

/**
 * Per-body toughness for everything that is not a boss.
 *
 * Four segments: 6.0%/round to 30, 9.5% to 60, 8.5% to 85, 5.5% to 100. Flat
 * for the first eight rounds — early difficulty comes from *new families*, not
 * from fatter grunts.
 *
 * Total at round 100 is ~940x. The first segment steepened this revision, 4.5%
 * to 6.0%, and the second eased to pay for it, so everything from round 31 on is
 * within a few percent of where it was; acts I and II were the trade, because
 * their rounds were being answered by body *count* rather than by anything the
 * board had to think about. Steepening it to 20%/round across rounds 31–52 let the
 * generator reach its targets and cost act IV *half its coverage* — round-60
 * bodies became seven times fatter, so splash and chain caught a seventh as much
 * per shot, and the median fell from 1.68 to 0.86 at the same RBE. Body size is
 * a much sharper lever on coverage than round size is. See docs/BALANCE.md §2.3.
 *
 * The rest of the RBE curve's growth comes from the roster tiering up
 * (grunt 40hp → armoured.citadel 1,800hp) and from counts.
 * Splitting the growth three ways is what stops late rounds being either a
 * single unkillable body or a thousand trivial ones — and the middle segment is
 * as steep as it is precisely to hold that second failure off: at 7%/round the
 * generator queued 1,150 bodies for round 50, past `MAX_BODIES_PER_ROUND`.
 *
 * The steepness here is load-bearing in a way that is not obvious. RBE is
 * fixed by the curve above, so a *steeper* hp scale means *fewer* bodies for
 * the same difficulty, and fewer bodies means splash and chain towers catch
 * less per shot. Flatten this and the late game becomes a solved problem for
 * any board with two gravity wells on it. See docs/BALANCE.md.
 */
export function hpScale(round: number): number {
  const r = clampRound(round);
  if (r <= 8) return 1;
  let s = Math.pow(1.060, Math.min(r, 30) - 8);
  if (r > 30) s *= Math.pow(1.095, Math.min(r, 60) - 30);
  if (r > 60) s *= Math.pow(1.085, Math.min(r, 85) - 60);
  if (r > 85) s *= Math.pow(1.055, r - 85);
  return s;
}

/** Authored effective hit points for the boss on `round`, or 0 if there is none. */
export function bossRbe(round: number): number {
  return BOSS_RBE[clampRound(round)] ?? 0;
}

/**
 * Boss scale, solved so the boss body lands on {@link BOSS_RBE}.
 *
 * Returns 1 on non-boss rounds. This *overrides* the absolute hit points in
 * `EnemyCatalog` — those statlines are read as shapes (how much of the pool is
 * shield, how much armour, what it splits into), not as magnitudes.
 */
export function bossHpScale(round: number): number {
  const id = bossFor(round);
  if (!id) return 1;
  return bossRbe(round) / totalRbe(id);
}

/** The boss's realised fraction of its round. Reported, never authored. */
export function bossShare(round: number): number {
  const t = targetRbe(round);
  return t > 0 ? bossRbe(round) / t : 0;
}

/**
 * The scale a given archetype spawns at on a given round.
 *
 * `entities/Enemy.ts` currently registers a `(round) => number` hook, which
 * cannot express the boss split. Until that hook widens, `main.ts` should pass
 * {@link hpScale} to `setWaveHpScale` and this function is what the balance
 * tooling uses. See the integration note in docs/BALANCE.md.
 */
export function waveHpScaleFor(round: number, archetypeId: string): number {
  return enemyById(archetypeId).family === 'boss' ? bossHpScale(round) : hpScale(round);
}

// ───────────────────────────────────────────────────────────────── roster ──

export interface RosterEntry {
  id: string;
  /** Round this archetype is introduced, alone and in small numbers. */
  unlock: number;
  /** Last round it can be picked. Retiring a tier stops round 90 being grunts. */
  retire: number;
  /** Base selection weight once it is a regular. */
  weight: number;
  /** Spawn as one flocking blob rather than a queue. */
  cluster: boolean;
}

/**
 * The curriculum. Order is the order the player meets things in.
 *
 * Two rules govern the unlock rounds, and the second one is not obvious:
 *
 * 1. Never introduce a family on a boss round or the round after one.
 * 2. **Never introduce a family on a round the rhythm already made quiet.**
 *    Debut rounds carry `BEAT.debut` (0.85). Land one on a `breather` round
 *    (0.80) and the two multiply to 0.68, which digs a trough deep enough that
 *    the RBE curve stops being monotone over any reasonable window. Every
 *    unlock below sits on `round % 10 ∈ {1, 2, 4, 7, 9}` — the neutral slots.
 *
 * A player learning `shielded` at round 29 has round 30's decade step waiting
 * to ask whether they worked out that burst damage is the wrong answer.
 *
 * Since acts landed the schedule carries a third rule: **an act introduces its
 * own families and no others.** `Levels.ACTS[i].debuts` names them and
 * `tests/levels.test.ts` asserts the two lists agree, so a roster edit that
 * quietly moves a family into the wrong act fails rather than drifting. The
 * three families added for acts V–VII (`splitter.leviathan`,
 * `armoured.citadel`, `runner.vector`, `weaver.eclipse`) exist because the last
 * forty rounds need statlines the earlier roster cannot reach: at act VII's RBE
 * a group of hornets is nine hundred bodies, and the body cap says no.
 */
export const ROSTER: readonly RosterEntry[] = [
  { id: 'grunt', unlock: 1, retire: 46, weight: 1.00, cluster: false },
  { id: 'runner', unlock: 4, retire: 40, weight: 0.90, cluster: false },
  { id: 'swarmling', unlock: 7, retire: 48, weight: 1.50, cluster: true },
  { id: 'grunt.heavy', unlock: 9, retire: 70, weight: 1.00, cluster: false },
  { id: 'armoured', unlock: 15, retire: 50, weight: 0.90, cluster: false },
  { id: 'weaver', unlock: 17, retire: 52, weight: 0.90, cluster: false },
  { id: 'splitter.prime', unlock: 19, retire: 100, weight: 0.80, cluster: false },
  { id: 'swarmling.mite', unlock: 22, retire: 66, weight: 1.45, cluster: true },
  { id: 'runner.blink', unlock: 27, retire: 74, weight: 0.85, cluster: false },
  { id: 'grunt.monolith', unlock: 28, retire: 88, weight: 0.45, cluster: false },
  { id: 'shielded', unlock: 29, retire: 60, weight: 0.90, cluster: false },
  { id: 'swarmling.hornet', unlock: 32, retire: 100, weight: 1.50, cluster: true },
  { id: 'armoured.bulwark', unlock: 37, retire: 82, weight: 0.85, cluster: false },
  { id: 'weaver.phantom', unlock: 39, retire: 84, weight: 0.90, cluster: false },
  { id: 'grunt.elite', unlock: 42, retire: 100, weight: 1.00, cluster: false },
  { id: 'shielded.warden', unlock: 47, retire: 92, weight: 0.90, cluster: false },
  { id: 'swarmling.drone', unlock: 49, retire: 100, weight: 1.50, cluster: true },
  { id: 'runner.courier', unlock: 52, retire: 100, weight: 0.75, cluster: false },
  { id: 'weaver.mirage', unlock: 57, retire: 100, weight: 0.75, cluster: false },
  { id: 'armoured.siege', unlock: 59, retire: 100, weight: 0.90, cluster: false },
  { id: 'shielded.aegis', unlock: 62, retire: 100, weight: 0.90, cluster: false },
  { id: 'splitter.leviathan', unlock: 63, retire: 100, weight: 0.85, cluster: false },
  { id: 'armoured.citadel', unlock: 78, retire: 100, weight: 1.80, cluster: false },
  { id: 'runner.vector', unlock: 84, retire: 100, weight: 0.85, cluster: false },
  { id: 'weaver.eclipse', unlock: 92, retire: 100, weight: 4.20, cluster: false },
];

/** The archetype introduced on this round, if any. */
export function debutAt(round: number): RosterEntry | null {
  for (const e of ROSTER) if (e.unlock === round) return e;
  return null;
}

/** Everything legal on a round, debut included. */
export function availableAt(round: number): RosterEntry[] {
  const out: RosterEntry[] = [];
  for (const e of ROSTER) if (e.unlock <= round && e.retire >= round) out.push(e);
  return out;
}

/** Bodies a typical group holds. Only used to size the fitness term below. */
const TYPICAL_GROUP = 45;

/** Log-space width of the fitness curve. Wide enough to keep real variety. */
const FITNESS_SIGMA = 1.15;

/**
 * Selection weight for one archetype on one round.
 *
 * Two terms:
 *
 * 1. **Recency.** An archetype unlocked three rounds ago is roughly twice as
 *    likely to appear as one the player has known for thirty rounds. This is
 *    the "you will be seeing a lot of this" beat.
 * 2. **Fitness.** A gaussian in log space, centred on the effective hit points
 *    a group *should* be carrying this round. Without it, round 62 picks
 *    swarmling.mite, the group pins at its size cap, and the round lands at a
 *    fifth of its RBE target because 160 mites is the most mites you are
 *    allowed to spawn. The fitness term is what makes the roster tier up on its
 *    own instead of needing a hand-written schedule of which family belongs to
 *    which decade.
 *
 * Sigma is deliberately wide. Narrow it and every round becomes three groups of
 * whatever the single best-fitting archetype is.
 */
function weightAt(e: RosterEntry, round: number, idealUnit: number): number {
  const age = round - e.unlock;
  const recency = 1 + 1.2 * Math.exp(-age / 9);

  let fitness = 1;
  if (idealUnit > 0) {
    const unit = Math.max(1, totalRbe(e.id) * hpScale(round));
    const d = Math.log(unit / idealUnit) / FITNESS_SIGMA;
    fitness = Math.exp(-0.5 * d * d);
  }
  return e.weight * recency * Math.max(fitness, 0.02);
}

// ────────────────────────────────────────────────────────── construction ──

/**
 * Weighted sample without replacement. Consumes exactly `n` draws from `rng`.
 *
 * Damping the second fast pick is the only non-obvious part. {@link paceOf}
 * equalises demand *between* the groups of one round, but it cannot help a
 * round whose every pick is fast: there is nothing slow left to absorb the
 * budget, so the whole round arrives inside one short window. Round 61 was
 * exactly that — `run.courier`, `run.blink` and `wvr.phantom` together, three
 * groups averaging 2.5x grunt pace, and it came out 25% harder than any round
 * within twenty of it while reading as an ordinary breather.
 *
 * So a candidate's weight is divided by how fast the round already is. One fast
 * family per round is the intent; three is an accident of the draw.
 */
function sample(
  pool: RosterEntry[], n: number, round: number, idealUnit: number, rng: () => number,
): RosterEntry[] {
  const left = pool.slice();
  const out: RosterEntry[] = [];
  let paceSum = 0;
  const damp = (e: RosterEntry): number => {
    const pace = paceOf(e.id);
    if (pace <= 1 || out.length === 0) return 1;
    const meanSoFar = paceSum / out.length;
    return 1 / (1 + 1.6 * Math.max(0, meanSoFar - 1) * (pace - 1));
  };
  for (let k = 0; k < n && left.length > 0; k++) {
    let total = 0;
    for (const e of left) total += weightAt(e, round, idealUnit) * damp(e);
    let roll = rng() * total;
    let pick = left.length - 1;
    for (let i = 0; i < left.length; i++) {
      roll -= weightAt(left[i], round, idealUnit) * damp(left[i]);
      if (roll <= 0) { pick = i; break; }
    }
    out.push(left[pick]);
    paceSum += paceOf(left[pick].id);
    left.splice(pick, 1);
  }
  return out;
}

/**
 * How many distinct groups a round is built from.
 *
 * Debut rounds get one *fewer* group than usual, not two groups flat. The
 * earlier version capped them at two, and since the debut group itself is
 * capped at a token four bodies, the single filler group could not absorb a
 * round-50 budget before hitting its own size cap. Those rounds undershot
 * their RBE target by 6x, which showed up as trivially easy debut rounds
 * followed by 9x spikes into the round after. Low pressure on a debut round is
 * `BEAT.debut`'s job, not the group count's.
 */
function groupCount(round: number, debut: RosterEntry | null, rng: () => number): number {
  // Five, not three, and not four either. A boss round's escorts have to absorb
  // the whole round minus the boss body, and at three groups the caps bound
  // before the budget does — round 40 landed at 81% of its target, which broke
  // the dip the rhythm promises on the round after a decade step. Four fixed
  // round 40 and left round 20 a hair *over* round 21, which breaks the same dip
  // from the other side.
  if (isBossRound(round)) return 5;
  const base = 2 + Math.floor(round / 22);
  const jitter = rng() < 0.35 ? 1 : 0;
  const n = base + jitter - (debut ? 1 : 0);
  return n < 2 ? 2 : n > 5 ? 5 : n;
}

/** Speed the whole pace correction is expressed against: the round-1 grunt. */
const REFERENCE_SPEED = 34;

/** Centreline the demand weight defaults to: act IV's serpentine. */
const REFERENCE_TRACK = 1574;

/**
 * How much harder a point of RBE is when it is carried by *this* archetype.
 *
 * A group's real demand is not its hit points, it is its hit points divided by
 * the seconds it spends in front of the guns, and those seconds are
 * `TRACK_LENGTH / speed`. A `runner.courier` at 108px/s crosses the map three
 * times faster than a `grunt`, so an equal-RBE group of couriers asks for three
 * times the DPS.
 *
 * Budgeting purely by RBE ignored that, and it showed: in the report before this
 * correction, `runner.courier` was the binding group on 23 of the last 30 rounds
 * and `armoured` on every round from 12 to 22 — the roster's two speed extremes,
 * both mis-sized in opposite directions. Dividing each group's share of the
 * budget by its pace equalises the demand without moving the round's RBE, which
 * is still solved exactly: whatever the fast families give up, the slow ones
 * absorb.
 *
 * Clamped below 0.55 so the slowest archetypes (`armoured.siege` at 15px/s)
 * cannot swallow an entire round on their own.
 */
export function paceOf(id: string): number {
  return Math.max(0.55, enemyById(id).speed / REFERENCE_SPEED);
}

/** Damage types the resistance term averages over. Order is irrelevant. */
const DAMAGE_TYPES = ['kinetic', 'energy', 'explosive', 'corrosive', 'arcane'] as const;

/**
 * Per-hit damage of the board a player is likely to be fielding on a round,
 * after the armour-pierce multiplier and before flat armour is subtracted.
 *
 * It has to be a curve rather than a constant, and act I is why. Flat armour is
 * a *fraction* of a hit, and the fraction depends entirely on how big the hit
 * is: armour 4 against act I's four-damage turret is total immunity, and against
 * act VI's hundred-and-fifty-damage capstone it is a rounding error. A single
 * reference figure priced `armoured` as a 9% tax on the round it debuts, the
 * generator happily sent sixteen of them at round 10, and no board act I can
 * build removed any of it — measured coverage 0.55 with fourteen lives lost on
 * one round.
 *
 * Anchors are the median per-hit of the boards `Balance.buildForBudget` actually
 * assembles at each round, rounded. Interpolated in log space like everything
 * else in this file.
 */
const HIT_ANCHORS: readonly (readonly [number, number])[] = [
  [1, 5],
  [12, 12],
  [24, 26],
  [40, 52],
  [60, 90],
  [80, 130],
  [100, 175],
];

function refHit(round: number): number {
  const r = clampRound(round);
  for (let i = 1; i < HIT_ANCHORS.length; i++) {
    const [r1, v1] = HIT_ANCHORS[i];
    if (r > r1) continue;
    const [r0, v0] = HIT_ANCHORS[i - 1];
    const t = (r - r0) / (r1 - r0);
    return Math.exp(Math.log(v0) + t * (Math.log(v1) - Math.log(v0)));
  }
  return HIT_ANCHORS[HIT_ANCHORS.length - 1][1];
}

/** How far above linear the pace term runs. See {@link demandOf}. */
const PACE_EXPONENT = 1.30;

/** How much of a shield's regeneration the demand weight charges for. A guess. */
const SHIELD_TAX_K = 0.90;

const MAX_ARMOUR_TAX = 2.6;
const MAX_SHIELD_TAX = 4.5;

/**
 * How steeply a body's *mass* is charged for, and the band it is charged in.
 *
 * Splash, pierce and chain multiply targets rather than damage, so a point of
 * RBE carried by one fat body is worth several carried by a queue of thin ones.
 * docs/BALANCE.md §2.3 measured this the hard way: steepening `hpScale` across
 * rounds 31–52 halved act IV's coverage at *identical* RBE, purely because the
 * bodies got seven times fatter and every splash radius caught a seventh as
 * much. Body size is the sharpest lever on coverage in the file and the composer
 * could not see it at all.
 *
 * The exponent is well under 1 because the effect saturates: past the point
 * where a splash radius holds one body, making that body heavier costs the
 * defence nothing more.
 */
const MASS_TAX_K = 0.42;
const MIN_MASS_TAX = 0.80;
const MAX_MASS_TAX = 2.4;

const MASS_REFERENCE = new Map<number, number>();

/**
 * The mass a body on this round is measured against: the geometric mean of the
 * effective hit points of every archetype the round may legally draw.
 *
 * Derived rather than authored, and it has to be, because the roster tiers up.
 * Ninety hit points is a heavy body on round 12 and a rounding error on round
 * 80; a fixed reference would price `grunt.heavy` as a monster for the whole
 * first act and as free for the rest of the game.
 */
function refMass(round: number): number {
  const r = clampRound(round);
  const held = MASS_REFERENCE.get(r);
  if (held !== undefined) return held;
  const pool = availableAt(r);
  let sum = 0;
  for (const e of pool) sum += Math.log(Math.max(1, totalRbe(e.id)));
  const mean = pool.length > 0 ? Math.exp(sum / pool.length) : 1;
  MASS_REFERENCE.set(r, mean);
  return mean;
}

/**
 * How much harder a point of RBE is when *this* archetype carries it.
 *
 * {@link paceOf} was the first half of this and shipped alone. docs/BALANCE.md
 * §7.3 measured what it missed: round-to-round coverage varied ±40% because
 * "speed" is only one of four ways a body can be expensive, and the composer
 * could not see the other three. A round drawing `shielded.aegis` — 440 shield
 * regenerating at 60/s behind an energy resistance of 0.5 — was far harder per
 * point of RBE than one drawing `grunt.elite`, and both were budgeted the same.
 *
 * Four terms, multiplied:
 *
 * 1. **Pace.** `speed / 34`, floored at 0.55. Seconds in front of the guns.
 * 2. **Armour.** Flat reduction lands *per hit*, so it does not scale with the
 *    body's hit points at all: armour 24 against a 46-damage reference shot
 *    costs half of every hit. `1 / (1 - armour / REF_HIT)`, capped at 2.4 so a
 *    citadel cannot swallow a whole round on its own.
 * 3. **Shield regeneration.** RBE counts the shield *pool* and nothing else, so
 *    a body that regenerates its pool twice over during its transit is priced at
 *    a third of what it costs. Charged as
 *    `1 + K × regen × window / rbe`, where `window` is 60% of the transit — the
 *    fraction of it the body actually spends being shot at. Scale-free: the game
 *    scales regeneration by `hpScale` alongside hit points, so the ratio holds
 *    at every round.
 * 4. **Resistance.** The mean of the five damage-type multipliers, inverted. A
 *    body resistant across the board makes every tower on the map worse, and the
 *    generator had no way to know.
 * 5. **Mass.** Effective hit points against the round's median body, at
 *    { MASS_TAX_K}. Splash and pierce multiply *targets*, so the same RBE
 *    delivered as thirty fat bodies is far more expensive than as ninety thin
 *    ones. Without it the top-up pass — which ranks by RBE per unit of demand —
 *    reads the heaviest archetype on the shelf as the cheapest way to fill a
 *    round, and act III fills itself with ninety monoliths.
 *
 * The round's RBE does not move — the budget is redistributed, not enlarged, so
 * whatever the expensive families give up the cheap ones absorb. What changes is
 * that a round of aegis is now *fewer* aegis.
 */
export function demandOf(
  id: string, trackLength: number = REFERENCE_TRACK, round = 50,
): number {
  const a = enemyById(id);
  // Pace enters above linear. The seconds a body spends under fire fall with
  // its speed, which is the linear part; what the linear part misses is that a
  // fast group is also a *thin* group — fewer bodies on the track at once, so
  // every splash radius and pierce line catches less of it. `runner.courier`
  // was the binding group on rounds 69 and 70 at coverage 0.87 with the linear
  // term already applied.
  const pace = Math.pow(paceOf(id), PACE_EXPONENT);

  const hit = refHit(round);
  const armourTax = a.armour > 0
    ? Math.min(MAX_ARMOUR_TAX, 1 / Math.max(1 / MAX_ARMOUR_TAX, 1 - a.armour / hit))
    : 1;

  let shieldTax = 1;
  if (a.shield > 0 && a.shieldRegenPerSec > 0) {
    const window = (trackLength / a.speed) * 0.6;
    const rbe = Math.max(1, totalRbe(id));
    shieldTax = Math.min(MAX_SHIELD_TAX, 1 + SHIELD_TAX_K * a.shieldRegenPerSec * window / rbe);
  }

  let resistSum = 0;
  for (const t of DAMAGE_TYPES) resistSum += a.resist?.[t] ?? 1;
  const resistTax = DAMAGE_TYPES.length / Math.max(0.2, resistSum);

  const massTax = Math.min(MAX_MASS_TAX, Math.max(
    MIN_MASS_TAX, Math.pow(Math.max(1, totalRbe(id)) / refMass(round), MASS_TAX_K),
  ));

  return pace * armourTax * shieldTax * resistTax * massTax;
}

/**
 * Solves group counts against an RBE budget.
 *
 * Two passes: allocate by weight and pace, cap oversized groups, then
 * redistribute what the caps refused onto the groups that still have headroom.
 * Without the second pass a round whose composition includes swarmlings
 * undershoots its target by a third, because 250 mites is the only way to spend
 * the budget on 4hp bodies.
 */
function solveCounts(
  picks: RosterEntry[], budget: number, scale: number, track: number, round: number,
): number[] {
  const n = picks.length;
  const unit: number[] = new Array(n);
  const cap: number[] = new Array(n);
  const share: number[] = new Array(n);
  let weightSum = 0;

  for (let i = 0; i < n; i++) {
    unit[i] = Math.max(1, totalRbe(picks[i].id) * scale);
    // Splitters produce 33 bodies each, so their cap is a body cap, not a
    // spawn cap. Without this a round of 37 primes queues 1,200 bodies.
    const byBodies = Math.floor(CAP_BODIES_PER_GROUP / Math.max(1, totalBodies(picks[i].id)));
    // ...and a third cap, on the *budget* rather than on the bodies. Ninety is
    // a readability limit that says nothing about how much of a round ninety
    // bodies are: ninety `swarmling.mite` is a rounding error and ninety
    // `grunt.monolith` was three quarters of round 36. A group over
    // {@link groupShare} of its round is a wall rather than a wave, and the
    // model prices it as one, because splash and pierce have nothing else on the
    // track to catch. What this refuses, the redistribution pass and the top-up
    // below spend on the rest of the round.
    const byShare = Math.floor((budget * groupShare(n)) / unit[i]);
    cap[i] = Math.max(1, Math.min(groupCap(picks[i].id, picks[i].cluster, round), byBodies, byShare));
    share[i] = picks[i].weight / demandOf(picks[i].id, track, round);
    weightSum += share[i];
  }

  const counts: number[] = new Array(n).fill(0);
  const ceiling: number[] = new Array(n);
  let spare = 0;
  for (let i = 0; i < n; i++) {
    const want = (budget * share[i]) / weightSum / unit[i];
    const c = Math.max(1, Math.round(want));
    // What the redistribution below may grow this group to. A group the demand
    // weight sized at twenty must not come back at ninety just because it was
    // the only one with room left — that is the weighting being undone by its
    // own leftovers. Whatever will not fit inside these ceilings goes to the
    // top-up, which spends it on *another* group rather than on a bigger one.
    ceiling[i] = Math.min(cap[i], Math.max(c * 2, c + 6));
    if (c > cap[i]) {
      spare += (c - cap[i]) * unit[i];
      counts[i] = cap[i];
    } else {
      counts[i] = c;
    }
  }

  // Redistribution. What the caps refused goes back out on the *same* demand
  // weights the first pass used, not to whoever has the most headroom.
  //
  // Filling the largest headroom first quietly undid the weighting: on round 34
  // the composer allotted `shielded` twenty-two bodies, three of the four other
  // groups then pinned at their caps, and the whole remainder landed on the one
  // group with room — ninety regenerating bodies, which is the round the demand
  // weight had just decided not to build. Several small passes because a
  // proportional share can exceed one group's remaining room.
  for (let pass = 0; pass < 3 && spare > 0; pass++) {
    let open = 0;
    for (let i = 0; i < n; i++) if (ceiling[i] > counts[i]) open += share[i];
    if (open <= 0) break;
    let spent = 0;
    for (let i = 0; i < n; i++) {
      const free = ceiling[i] - counts[i];
      if (free <= 0) continue;
      const add = Math.min(free, Math.floor((spare * share[i]) / open / unit[i]));
      if (add <= 0) continue;
      counts[i] += add;
      spent += add * unit[i];
    }
    if (spent <= 0) break;
    spare -= spent;
  }

  return counts;
}

/**
 * Builds one round.
 *
 * `rng` must be a stream derived from `seed:round`, not a shared stream, so
 * round 57 is identical whether you generated rounds 1..57 or asked for 57 on
 * its own. That property is what makes the balance report reproducible.
 */
export function buildWave(round: number, rng: () => number): Wave {
  const r = clampRound(round);
  const debut = debutAt(r);
  const boss = bossFor(r);
  const scale = hpScale(r);
  const target = targetRbe(r);
  // The act's centreline, because the demand weight prices shield regeneration
  // against the seconds a body spends crossing it. A maelstrom aegis
  // regenerates for nearly three times as long as an outpost one.
  const track = actOf(r).trackLength;

  const groups: WaveGroup[] = [];
  let bossPool = 0;

  if (boss) {
    bossPool = totalRbe(boss) * bossHpScale(r);
    groups.push({ archetypeId: boss, count: 1, interval: 0, delay: 4 });
  }

  const budget = Math.max(1, target - bossPool);
  const pool = availableAt(r);
  const wanted = groupCount(r, debut, rng);
  /** Effective hit points a group ought to be carrying. Drives fitness. */
  const idealUnit = budget / (wanted * TYPICAL_GROUP);

  let picks: RosterEntry[];
  if (debut) {
    // Introduction rounds: the new family in a token count, and familiar
    // filler so the round is not just a thing you have never seen before.
    const rest = pool.filter((e) => e.id !== debut.id);
    picks = [debut, ...sample(rest, Math.max(0, wanted - 1), r, idealUnit, rng)];
  } else {
    picks = sample(pool, wanted, r, idealUnit, rng);
  }

  let counts: number[];
  if (debut) {
    // The debut group is capped at a handful regardless of budget. The rest of
    // the round absorbs what is left.
    const tokenUnit = Math.max(1, totalRbe(debut.id) * scale);
    const token = Math.min(4, Math.max(2, Math.round((budget * 0.18) / tokenUnit)));
    const rest = picks.slice(1);
    const restCounts = rest.length > 0
      ? solveCounts(rest, budget - token * tokenUnit, scale, track, r)
      : [];
    counts = [token, ...restCounts];
  } else {
    counts = solveCounts(picks, budget, scale, track, r);
  }

  // Round-wide overshield budget.
  //
  // {@link CAP_REGEN} bounds one *group* and nothing bounded the *round*, which
  // is the same error one level up. Round 52 drew `shielded.warden` and
  // `shielded` together, both landed just under the group cap, and the round
  // arrived carrying eighty-seven regenerating bodies — very nearly twice what
  // the cap exists to allow. A board of twenty-four towers with three quarters
  // of a million sunk into it lost a hundred and ten lives on it, measured. The
  // reasoning is `CAP_REGEN`'s own: suppression is a *rate*, the hits a board has
  // are fixed, and every extra regenerating body on the track divides them
  // again. It does not matter which queue the body walked in with.
  let regenLeft = Math.max(8, Math.round(CAP_REGEN * capScale(r)));
  for (let i = 0; i < picks.length; i++) {
    if (!regenerates(picks[i].id)) continue;
    counts[i] = Math.max(1, Math.min(counts[i], regenLeft));
    regenLeft = Math.max(0, regenLeft - counts[i]);
  }

  // Top-up. Fitness weighting keeps the picks appropriate, but a round can
  // still draw three cheap families and pin all of them at their size caps, at
  // which point the leftover budget has nowhere to go and the round lands at a
  // fraction of its target. Rather than let that pass silently, spend the
  // remainder on extra groups of the heaviest thing available. A second group
  // of an archetype already present is fine — it arrives on its own delay, so
  // it reads as a second push rather than as a bookkeeping artefact.
  {
    // Heaviest per unit of pace, for the same reason `solveCounts` divides by
    // it: topping a round up with the fastest thing available turns leftover
    // budget into a DPS wall rather than into more of the round.
    // Never the debut family. The debut group is deliberately capped at four
    // bodies, and this loop used to be free to pick the same archetype as its
    // filler — which made round 37 four `armoured.bulwark` *plus ninety more*,
    // coverage 0.64, and a hundred and four lives on the round that was supposed
    // to introduce them gently. The whole point of a debut is that you meet the
    // thing before you have to solve it.
    const filtered = debut ? pool.filter((e) => e.id !== debut.id) : pool;
    // Round 1 is the exception: `grunt` is the debut *and* the only family in
    // the pool, so excluding it leaves the round with two bodies in it.
    const fillable = filtered.length > 0 ? filtered : pool;
    const heaviest = fillable.slice().sort(
      (a, b) => totalRbe(b.id) / demandOf(b.id, track, r) - totalRbe(a.id) / demandOf(a.id, track, r),
    );
    let placed = 0;
    for (let guard = 0; guard < 6 && picks.length < 7; guard++) {
      let have = 0;
      for (let i = 0; i < picks.length; i++) have += counts[i] * totalRbe(picks[i].id) * scale;
      const short = budget - have;
      if (short <= budget * 0.06) break;

      const e = heaviest[placed % Math.max(1, heaviest.length)];
      placed++;
      if (!e) break;
      // The budget above is a property of the round, so it binds here too.
      if (regenerates(e.id) && regenLeft <= 0) continue;
      const unit = Math.max(1, totalRbe(e.id) * scale);
      const cap = Math.max(1, Math.min(
        groupCap(e.id, e.cluster, r),
        Math.floor(CAP_BODIES_PER_GROUP / Math.max(1, totalBodies(e.id))),
      ));
      // No single top-up group may carry more than {@link TOP_UP_SHARE} of the
      // round. Without the bound this loop hands the whole shortfall to the one
      // archetype at the top of the sort, and once act III owned a body heavy
      // enough to absorb it that is exactly what happened: ninety monoliths,
      // three quarters of round 36 in one queue, coverage 0.75. Splitting the
      // remainder across the next few entries fills the same budget out of
      // bodies a splash radius can still be pointed at.
      // The bound has to relax on a round that has almost nothing in it yet.
      // A fifth of round 1 is one grunt, so a flat share turned the opening
      // round into seven groups of one body each, filling the budget a grunt at
      // a time. One over the number of groups already placed is the share that
      // would make them equal, and never less than the flat bound.
      const share = Math.max(TOP_UP_SHARE, 1 / Math.max(1, picks.length));
      const byShare = Math.max(1, Math.floor((budget * share) / unit));
      let add = Math.max(1, Math.min(cap, byShare, Math.round(short / unit)));
      if (regenerates(e.id)) {
        add = Math.min(add, regenLeft);
        if (add <= 0) continue;
        regenLeft -= add;
      }
      picks.push(e);
      counts.push(add);
    }
  }

  // Stagger. Groups do not start together, and a group's internal spacing
  // depends on whether it is a queue or a blob, so a round has a rhythm.
  let cursor = boss ? 2.5 : 0;
  for (let i = 0; i < picks.length; i++) {
    const e = picks[i];
    const count = counts[i];
    const interval = e.cluster
      ? 0.04 + rng() * 0.05
      : Math.min(1.6, Math.max(0.09, SPAWN_WINDOW / Math.max(1, count)));
    groups.push({
      archetypeId: e.id,
      count,
      interval: Number(interval.toFixed(3)),
      delay: Number(cursor.toFixed(2)),
      ...(e.cluster ? { cluster: true } : {}),
    });
    cursor += 1.2 + rng() * 2.4;
  }

  const wave: Wave = { round: r, groups, rbe: 0 };
  if (boss) wave.boss = boss;
  wave.rbe = Math.round(waveRbe(wave));
  return wave;
}

/** Actual RBE of a built wave, from its integer counts. */
export function waveRbe(wave: Wave): number {
  let sum = 0;
  for (const g of wave.groups) {
    const scale = waveHpScaleFor(wave.round, g.archetypeId);
    sum += g.count * totalRbe(g.archetypeId) * scale;
  }
  return sum;
}

/**
 * Cash a round pays out if fully cleared.
 *
 * Deliberately *not* the RBE, for two reasons:
 *
 * 1. `Projectiles.resolve` awards cash on hit points removed, and shield
 *    absorption is scored for the damage counter but not for the wallet. A
 *    round of shielded.aegis is much poorer than its RBE suggests, which is the
 *    intended pressure — you pay to break shields.
 * 2. `Economy.payoutScale` deflates the rate as the round number climbs, so
 *    income tracks tower prices rather than enemy hit points.
 */
export function wavePayout(wave: Wave): number {
  const rate = payoutScale(wave.round);
  let sum = 0;
  for (const g of wave.groups) {
    const scale = waveHpScaleFor(wave.round, g.archetypeId);
    sum += g.count * hpOnly(g.archetypeId) * scale;
  }
  return sum * rate;
}

/** Bounty-weighted hit points of one spawn and its whole split chain. Excludes shields. */
function hpOnly(id: string, depth = 0): number {
  if (depth > 8) return 0;
  const a = enemyById(id);
  let n = a.hp * a.bounty;
  if (a.spawnsOnDeath) {
    for (const s of a.spawnsOnDeath) n += s.count * hpOnly(s.id, depth + 1);
  }
  return n;
}

/** Bodies a round produces, splitter children included. */
export function waveBodies(wave: Wave): number {
  let sum = 0;
  for (const g of wave.groups) sum += g.count * totalBodies(g.archetypeId);
  return sum;
}

/** Seconds from round start until the last spawn is queued. */
export function waveDuration(wave: Wave): number {
  let end = 0;
  for (const g of wave.groups) {
    const t = g.delay + Math.max(0, g.count - 1) * g.interval;
    if (t > end) end = t;
  }
  return end;
}

// ─────────────────────────────────────────────────────────────── the list ──

export const DEFAULT_WAVE_SEED = 'pixel-td';

/**
 * All hundred rounds. Each round draws from its own stream, so the list is
 * order-independent and a change to round 12's generation cannot move round 80.
 */
export function generateWaves(seed: string = DEFAULT_WAVE_SEED): Wave[] {
  const out: Wave[] = [];
  for (let r = 1; r <= WAVES.count; r++) {
    out.push(buildWave(r, seedFrom(`${seed}:${r}`)));
  }
  return out;
}

/** The RBE curve, index 0 = round 1. Handy for the danger meter and the tests. */
export function rbeCurve(waves: readonly Wave[]): number[] {
  return waves.map((w) => w.rbe);
}

// ───────────────────────────────────────────────────────── the director ──

/**
 * One archetype's share of an incoming round, for the wave bar.
 *
 * Declared here rather than imported so `Waves.ts` keeps its promise not to
 * depend on `main`'s files. It is structurally identical to `RoundEntry` in
 * `game/Session.ts`, which is what lets {@link WaveDirector} be handed to
 * `createRoundDriver` with no adapter.
 */
export interface RoundEntry {
  glyph: string;
  colour: PhosphorName;
  count: number;
  name: string;
}

/** Everything the wave bar needs. Matches `Session.RoundView` structurally. */
export interface RoundView {
  prep: boolean;
  progress: number;
  prepRemaining: number;
  incoming: readonly RoundEntry[];
  boss: string | null;
  canStartEarly: boolean;
  earlyBonus: number;
}

/**
 * Drives prep → combat → prep and detects round completion.
 *
 * A round is over when every group has been queued, `world.pending` is empty
 * and nothing is alive. All three conditions matter: pending can be empty
 * mid-round while a late group still waits on its delay, and the enemy array
 * holds bodies for a frame after they die.
 *
 * Implements `Session.RoundDriver` structurally, so `main.ts` can replace the
 * stub driver with `new WaveDirector()` and delete nothing else.
 */
export class WaveDirector {
  readonly name = 'waves';
  readonly waves: Wave[];
  /** Seconds left in the current prep phase. */
  prepLeft: number = WAVES.firstPrepSeconds;
  /** Prep seconds skipped by the last early start. Drives the early bonus. */
  lastEarlySeconds = 0;
  /** Sim time the current round finishes queueing spawns. */
  spawnEndsAt = 0;

  /** Spawns queued for the current round. Drives the wave-bar progress bar. */
  private queued = 0;
  private earlyRequest = false;

  constructor(seed: string = DEFAULT_WAVE_SEED) {
    this.waves = generateWaves(seed);
  }

  /** The wave the player is fighting, or is about to fight during prep. */
  current(world: World): Wave | null {
    const i = world.phase === 'combat' ? world.round - 1 : world.round;
    return this.waves[i] ?? null;
  }

  waveAt(round: number): Wave | null {
    return this.waves[round - 1] ?? null;
  }

  reset(world: World): void {
    world.round = 0;
    world.pending.length = 0;
    world.phase = 'prep';
    this.prepLeft = WAVES.firstPrepSeconds;
    this.lastEarlySeconds = 0;
    this.spawnEndsAt = 0;
    this.queued = 0;
    this.earlyRequest = false;
  }

  /** The "start now" button. Honoured on the next step, never mid-combat. */
  requestEarlyStart(): void {
    this.earlyRequest = true;
  }

  /**
   * Player pressed send. Begins the round immediately and returns the bonus.
   * Refuses outside prep, so a double-click cannot skip a round.
   */
  startEarly(world: World): number {
    if (world.phase !== 'prep') return 0;
    this.lastEarlySeconds = Math.max(0, this.prepLeft);
    this.earlyRequest = false;
    const before = world.cash;
    this.begin(world);
    return world.cash - before;
  }

  finished(world: World): boolean {
    return world.phase === 'victory' || world.round >= WAVES.count;
  }

  /** Cash the player would collect by starting right now. */
  earlyBonusNow(world: World): number {
    return world.phase === 'prep' ? earlyStartBonus(Math.max(0, this.prepLeft)) : 0;
  }

  /** Per-frame summary for the wave bar. Allocates; call once per frame, not per step. */
  view(world: World): RoundView {
    const prep = world.phase === 'prep';
    const wave = this.current(world);
    const incoming: RoundEntry[] = [];
    if (wave) {
      for (const g of wave.groups) {
        const a = enemyById(g.archetypeId);
        incoming.push({ glyph: a.glyph, colour: a.colour, count: g.count, name: a.id });
      }
    }

    const total = world.round <= 0 ? WAVES.firstPrepSeconds : WAVES.prepSeconds;
    let progress: number;
    if (prep) {
      progress = 1 - Math.max(0, this.prepLeft) / Math.max(0.001, total);
    } else if (this.queued > 0) {
      progress = 1 - world.pending.length / this.queued;
    } else {
      progress = 1;
    }

    return {
      prep,
      progress: progress < 0 ? 0 : progress > 1 ? 1 : progress,
      prepRemaining: Math.max(0, this.prepLeft),
      incoming,
      boss: wave?.boss ?? null,
      canStartEarly: prep && world.round < WAVES.count,
      earlyBonus: this.earlyBonusNow(world),
    };
  }

  step(dt: number, world: World): void {
    checkTransitions(world);
    if (world.phase !== 'prep' && world.phase !== 'combat') return;

    if (world.phase === 'prep') {
      this.prepLeft -= dt;
      if (this.earlyRequest || this.prepLeft <= 0) {
        this.lastEarlySeconds = this.earlyRequest ? Math.max(0, this.prepLeft) : 0;
        this.earlyRequest = false;
        this.begin(world);
      }
      return;
    }

    if (world.time < this.spawnEndsAt) return;
    if (world.pending.length > 0) return;
    for (let i = 0; i < world.enemies.length; i++) {
      if (world.enemies[i].alive) return;
    }
    this.complete(world);
  }

  private begin(world: World): void {
    const wave = this.waves[world.round];
    if (!wave) { world.phase = 'victory'; return; }
    world.round = wave.round;
    this.queued = queueWave(wave, world);
    this.spawnEndsAt = world.time + waveDuration(wave);
    world.phase = 'combat';
    if (this.lastEarlySeconds > 0) awardEarlyStart(world, this.lastEarlySeconds);
  }

  private complete(world: World): void {
    awardRoundBonus(world, world.round);
    if (world.round >= WAVES.count) {
      world.phase = 'victory';
      return;
    }
    world.phase = 'prep';
    // The round about to be prepped is `world.round + 1`. If that opens an act
    // the player is rebuilding from nothing on a map they have not seen, and six
    // seconds is the wrong window for that.
    this.prepLeft = isActOpening(world.round + 1) ? WAVES.actPrepSeconds : WAVES.prepSeconds;
    this.lastEarlySeconds = 0;
  }
}

/**
 * Pushes every spawn of a wave into `world.pending` at absolute sim times.
 * Returns how many were queued, which is the denominator of the wave bar.
 */
export function queueWave(wave: Wave, world: World): number {
  const t0 = world.time;
  let n = 0;
  for (const g of wave.groups) {
    const cluster = g.cluster === true;
    for (let i = 0; i < g.count; i++) {
      world.pending.push({
        archetypeId: g.archetypeId,
        at: t0 + g.delay + i * g.interval,
        cluster,
      });
      n++;
    }
  }
  return n;
}

let director: WaveDirector | null = null;

/** The process-wide director. `main.ts` owns its lifetime; tests build their own. */
export function waveDirector(seed?: string): WaveDirector {
  if (!director || seed !== undefined) director = new WaveDirector(seed);
  return director;
}

/** The `System.step` entry point. Lazily builds the default director. */
export function stepWaves(dt: number, world: World): void {
  (director ?? (director = new WaveDirector())).step(dt, world);
}

export function resetWaves(world: World, seed?: string): WaveDirector {
  const d = waveDirector(seed ?? DEFAULT_WAVE_SEED);
  d.reset(world);
  return d;
}
